<template>
  <base-modal
    :title="$t('message.2fa.protectYourAccount')"
    :subtitle="$t('message.2fa.active2FA')"
    hook-sufix="Authentication2faSms"
    modal-width="600px"
    minHeight="300px"
    :hasHelper="{
      text: $t('message.2fa.knowMore'),
      link: helperLink,
    }"
    disableDefaultPadding
    twoFactorModal
    persistent
    :content-class="{
      'auth-2fa-sms-modal--required': isRequired2faFromOrganization,
      'auth-2fa-sms-modal': true,
    }"
  >
    <template #content>
      <div></div>
      <v-divider></v-divider>
      <v-row class="pa-6">
        <v-col cols="12" class="d-flex flex-column justify-center">
          <span class="body-label-caption stepCount">
            {{ $t('message.2fa.stepOne') }}
          </span>
          <p class="text-body-2 mb-0 body-regular-small stepTitle">
            {{ $t('message.2fa.stepOneSmsTitle') }}
          </p>
          <p class="text-body-2 mb-2 body-regular-small stepSubtitle">
            {{ $t('message.2fa.stepOneSmsSubtitle') }}
          </p>

          <v-col
            class="pa-0 pb-6 d-flex phone-input"
            cols="12"
            style="position: relative; z-index: 99"
          >
            <VuePhoneNumberInput
              no-example
              default-country-code="BR"
              :dark="$vuetify.theme.dark"
              clearable
              :color="$vuetify.theme.dark ? '#171924' : '#fff'"
              :translations="translationsPhoneInput"
              :error="!isValidPhoneNumber"
              :valid-color="$vuetify.theme.dark ? '#171924' : '#fff'"
              :error-color="$vuetify.theme.dark ? '#171924' : '#fff'"
              :preferred-countries="['BR', 'US']"
              @phone-number-focused="handleFocus"
              @phone-country-focused="handleFocus"
              @update="checkIfIsValidPhoneNumber"
              v-model="clientPhone"
              @blur="checkErrorisValidPhoneNumber"
            />
            <base-btn
              v-if="countdown === 0"
              class="ml-4"
              type="secondary"
              @click="sendCode"
              :disabled="isLoading"
            >
              {{ $t('message.2fa.sendCode') }}
            </base-btn>
            <span v-else class="ml-4 codTemporizer body-regular-smaller">
              {{ $t('message.2fa.resendCode') }} {{ countdown }}
              {{ $t('message.2fa.seconds') }}
            </span>
          </v-col>
        </v-col>
      </v-row>

      <v-divider></v-divider>

      <v-row class="pa-6">
        <v-col cols="12" class="d-flex flex-column justify-center">
          <span class="body-label-caption stepCount">{{
            $t('message.2fa.stepTwo')
          }}</span>
          <p class="body-regular-small mb-0 stepTitle">
            {{ $t('message.2fa.stepTwoSmsTitle') }}
          </p>
          <p class="body-regular-small mb-2 stepSubtitle">
            {{ $t('message.2fa.stepTwoExplanation') }}
          </p>
          <v-otp-input
            v-model="codeApp"
            :length="6"
            @finish="handleAction"
            type="number"
            ref="auth2faSmsModalInput"
            autocomplete="one-time-code"
            @focusin="otpRememberIndex"
            @keydown.stop="otpHandleKeydown"
          ></v-otp-input>
        </v-col>
      </v-row>
    </template>

    <template #card-actions>
      <v-divider></v-divider>
      <v-card-actions class="pa-6 d-flex justify-space-between">
        <base-btn type="neutral" text @click="backToOptions">
          {{ $t('message.2fa.otherWay') }}
        </base-btn>
        <base-btn
          v-if="!isRequired2faFromOrganization"
          type="tertiary"
          :loading="isLoading"
          :disabled="isLoading"
          @click="handleCancel"
        >
          {{ $t('message.2fa.cancel') }}
        </base-btn>
      </v-card-actions>
    </template>
  </base-modal>
</template>

<script>
import BaseModal from '../BaseModal.vue'
import VuePhoneNumberInput from 'vue-phone-number-input'
import twoFactorAuthApi from '@/services/twoFactorAuthApi'
import linkHelper from '@/utils/linkHelper.mixin.js'
import otpInputHandler from '@/utils/TwoFactorAuthMixins/otpInputHandler.mixin.js'

export default {
  name: 'Authentication2faSms',
  mixins: [linkHelper, otpInputHandler],
  components: {
    BaseModal,
    VuePhoneNumberInput,
  },
  data: () => ({
    phoneNumber: '',
    verificationCode: '',
    isLoading: false,
    showVerificationCode: false,
    phoneInternational: false,
    isValidPhoneNumber: true,
    clientPhone: '',
    codeApp: '',
    countdown: 0,
    countdownInterval: null,
    translationsPhoneInput: {
      countrySelectorLabel: 'Code pays',
      countrySelectorError: 'Choisir un pays',
      phoneNumberLabel: 'Número de telefone',
      example: 'Exemple :',
    },
    checkPhoneNumberIntervalId: null,
    rules: {
      required: (v) => !!v || 'Campo obrigatório',
      phone: (v) =>
        /^\+?[1-9]\d{1,14}$/.test(v) || 'Número de telefone inválido',
    },
    isRequired2faFromOrganization: false,
    opennedFromRequired2faModal: false,
    openned2faFromProfile: false,
  }),
  props: {
    otpRef: { default: 'auth2faSmsModalInput' },
    otpModelKey: { default: 'codeApp' },
  },
  computed: {
    isValid() {
      if (this.showVerificationCode) {
        return !!this.verificationCode
      }
      return !!this.phoneNumber && this.rules.phone(this.phoneNumber) === true
    },
    helperLink() {
      return this.enableTwoFactorAuthenticationHelper()
    },
    countdownText() {
      return this.$t('message.2fa.resendCode', { countdown: this.countdown })
    },
  },
  methods: {
    handleCancel() {
      this.$root.$emit('closeAuthentication2faSms')
    },
    backToOptions() {
      this.$root.$emit('closeAuthentication2faSms')
      this.$root.$emit('openTwoFactorAuth')
    },
    async handleAction() {
      if (!this.codeApp || this.codeApp.length !== 6) {
        return
      }

      this.isLoading = true
      try {
        await twoFactorAuthApi.verifySmsCode(this.codeApp)
        await twoFactorAuthApi.mfaSetup({ type: 'sms' })

        const currentUserInfo = this.$store.state.user.userAllInfo || {}
        const updatedUserInfo = {
          ...currentUserInfo,
          mfa_enable: true,
          mfa_methods: [...(currentUserInfo.mfa_methods || []), 'sms'],
        }
        this.$store.commit('user/UPDATE_USER_ALLINFO', updatedUserInfo)

        this.$root.$emit('closeAuthentication2faSms')
        this.$root.$emit('auth2faCreated')
        if (!this.opennedFromRequired2faModal && !this.openned2faFromProfile) {
          this.$root.$emit('openEnable2FAModal')
        }
        this.$root.$emit('updateMfaStatus', true)
      } catch (error) {
        console.error('Erro na verificação:', error)
      } finally {
        this.isLoading = false
      }
    },
    async sendCode() {
      if (!this.phoneInternational) {
        return
      }

      this.isLoading = true
      try {
        await twoFactorAuthApi.phoneRequestCode(this.phoneInternational)
        this.startCountdown()
      } catch (error) {
        console.error('Error sending code:', error)
      } finally {
        this.isLoading = false
        this.focusInput()
      }
    },
    startCountdown() {
      this.countdown = 30
      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--
          console.log('Countdown:', this.countdown)
        } else {
          clearInterval(this.countdownInterval)
        }
      }, 1000)
    },
    handleFocus() {
      const input = document.querySelector('.input-tel__input')
      if (input.style.boxShadow !== 'none') {
        input.style.boxShadow = 'none'
        input.style.caretColor = this.$vuetify.theme.dark ? '#757BA3' : '#000'
      }
    },
    checkIfIsValidPhoneNumber(e) {
      if (!e) return

      this.isValidPhoneNumber = e.isValid
      this.phoneInternational = e.e164
      this.handleFocus()
    },
    removeShadowWhenFocused() {
      const customInput = document.querySelector('.country-selector__input')
      const countrySelectorToggle = document.querySelector(
        '.country-selector__toggle'
      )
      const clickFlag = document.querySelector(
        '.country-selector__country-flag'
      )

      if (!customInput || !countrySelectorToggle || !clickFlag) {
        return
      }

      window.addEventListener('click', () => {
        if (customInput) {
          customInput.style.boxShadow = 'none'
        }
      })

      countrySelectorToggle.addEventListener('click', () => {
        if (customInput) {
          customInput.style.boxShadow = 'none'
        }
      })

      clickFlag.addEventListener('click', () => {
        if (customInput) {
          customInput.style.boxShadow = 'none'
        }
      })
    },
    checkErrorisValidPhoneNumber() {
      if (!this.isValidPhoneNumber) {
        this.createAccountError.invalidPhoneNumber = true
        return this.createAccountError.invalidPhoneNumber
      }
      return (this.createAccountError.invalidPhoneNumber = false)
    },
    focusInput() {
      this.$nextTick(() => {
        setTimeout(() => {
          const otpInput = this.$refs.auth2faSmsModalInput
          if (otpInput?.focus) {
            otpInput.focus()
          } else {
            otpInput?.$el.querySelector('input')?.focus()
          }
        }, 100)
      })
    },
  },
  mounted() {
    this.$root.$on('2faRequiredFromOrganization', (isRequired) => {
      this.isRequired2faFromOrganization = isRequired
    })
    this.$root.$on('opennedFromRequired2faModal', (val) => {
      this.opennedFromRequired2faModal = val
    })
    this.$root.$on('openned2faFromProfile', () => {
      this.openned2faFromProfile = true
    })
    this.removeShadowWhenFocused()
  },
  beforeDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
    this.$root.$off('2faRequiredFromOrganization')
    this.$root.$off('opennedFromRequired2faModal')
  },
}
</script>
<style lang="scss">
.country-selector.has-value .country-selector__input {
  padding-top: 0 !important;
}

.country-selector.has-value .country-selector__label {
  display: none !important;
}
.country-selector__country-flag {
  top: 14px !important;
}

.bg-dark .country-selector.has-hint .country-selector__input,
.bg-dark .country-selector.has-value .country-selector__input {
  background-color: $neutral-99-dark !important;
}
.iti-flag {
  border-radius: 50px !important;
}
.bg-dark .country-selector.has-hint .country-selector__input,
.bg-dark .country-selector.has-value .country-selector__input,
.bg-dark .vue-phone-number-input .input-tel__input[data-v-e59be3b4] {
  padding-top: 0 !important;
  &:hover {
    border: 1px $brand-80-light-dark solid !important;
  }
  &:focus-within {
    border: 2px $brand-60-base-dark solid !important;
  }
}
.country-selector.has-hint .country-selector__input,
.country-selector.has-value .country-selector__input,
.vue-phone-number-input .input-tel__input[data-v-e59be3b4] {
  &:hover {
    border: 1px $color-brand-light solid !important;
  }
  &:focus-within {
    border: 2px $color-brand solid !important;
  }
}
.country-selector .country-selector__list__item {
  color: #9b9b9b !important;
}
.input-tel.has-value .input-tel__input,
.input-tel.has-value .input-tel__input {
  padding-top: 0 !important;
}
.country-selector.has-value .country-selector__input,
.input-tel.is-valid .input-tel__input {
  border-color: varFigma(neutral-95-lightest) !important;
}
.input-tel.has-value {
  .input-tel__label {
    cursor: text;
    display: none;
  }
  .input-tel__clear {
    display: none;
  }
}
.vue-phone-number-input .select-country-container {
  max-width: 65px !important;
  min-width: 65px !important;
  width: 65px !important;
}
.auth-2fa-sms-modal {
  .theme--dark.v-otp-input .v-input .v-input__control .v-input__slot {
    background: transparent !important;
  }
}
.auth-2fa-sms-modal {
  .vue-phone-number-input.dark {
    .input-tel.input-phone-number.is-dark input {
      background-color: transparent !important;
      border-color: varFigma(neutral-95-lightest);
    }
  }
}
</style>
<style lang="scss" scoped>
.phone-input {
  flex: 0 !important;
}
.labelPhone {
  font-size: 12px;
  line-height: 12px;
  font-weight: 500;
  color: $neutral-20-dark-light;

  &.errorMsg {
    color: $feedback-danger-light;
    font-weight: 500;
  }
}

.errorMsg {
  line-height: 18px;
  font-size: 12px;
  font-weight: 400;
  color: $feedback-danger-light;
}

.qr-code-container {
  width: 200px;
  height: 200px;
  border: 1px solid var(--v-neutral-95-lightest);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
<style lang="scss" scoped>
.twoFactorModal {
  .codTemporizer {
    width: 124px;
    color: varFigma(neutral-40-light);
    text-align: center;
  }
  .stepCount {
    color: varFigma(neutral-50);
  }
  .stepSubtitle {
    color: varFigma(neutral-40-light);
  }
  .stepTitle {
    color: varFigma(neutral-20-dark);
  }
}
</style>
<style lang="scss">
.auth-2fa-sms-modal--required {
  .v-toolbar__items {
    display: none;
  }
}
</style>
